import {
  formatDayMonth,
  formatDateTime,
  type WorkRowModel,
  type WorkAction,
  type WorkSignifier,
} from "@excellent/ui";
import {
  Check,
  CornerUpLeft,
  ExternalLink,
  ArrowRight,
  CircleDot,
  GitPullRequest,
  AtSign,
  Lightbulb,
  Mail,
  Activity,
  ShieldAlert,
} from "@excellent/ui/icons";
import type { WorkItem, WorkKind, WorkStatus } from "./types";

/**
 * The ONE `WorkItem → WorkRowModel` mapping, shared by every surface that renders
 * the unified work feed (the Inbox `WorkSurface`, the Issues unified list).
 * Extracted so the row is defined once — the canonical work row (Matt,
 * 2026-07-16: "we need them all to look the same and have the same format"),
 * `docs/design/WORK-ROW-SYSTEM.md`.
 *
 * A task/review carries a lifecycle status → the StatusRing (WorkRow slot 2). A
 * status-less item (mention / suggestion / escalation / health / gate) leads with
 * its KIND glyph (slot 1) and says what it needs of you through the attention
 * mark. The "why it surfaced" rides the inline subtitle; the decision is the
 * hover action. `onOpen` is supplied by the caller (each surface owns its open
 * behavior — peek in place vs. navigate, and the Inbox's mark-read).
 *
 * ── Blocked, not just noticed (2026-07-23) ──────────────────────────────────
 * The three items that have literally stopped waiting on YOU — a task in
 * `review` (a different agent must verify it), an `escalation` (an agent handed
 * you a decision), a launch `gate` (an optimizer is paused on your call) — carry
 * the BLOCKED label + edge bar rather than a severity dot, so a list can be
 * scanned for "what is waiting on me" without reading a title (#43867). Health
 * problems stay NOTICES: broken, but not waiting on one specific act by you.
 */

/** The kind mark — slot 1 for a status-less item, and the distinguisher in a
 *  mixed inbox where a plain "task" glyph would otherwise repeat 40 times. */
const KIND_SIGNIFIER: Record<WorkKind, WorkSignifier> = {
  task: { as: "glyph", icon: CircleDot, label: "Task" },
  review: { as: "glyph", icon: GitPullRequest, label: "Review" },
  mention: { as: "glyph", icon: AtSign, label: "Mention" },
  suggestion: { as: "glyph", icon: Lightbulb, label: "Suggestion" },
  escalation: { as: "glyph", icon: Mail, label: "Escalation" },
  health: { as: "glyph", icon: Activity, label: "Health problem" },
  gate: { as: "glyph", icon: ShieldAlert, label: "Launch gate" },
};

/** The verb the date stamp abbreviates, per kind — "Shipped · Jul 23". */
const KIND_DATE_VERB: Partial<Record<WorkKind, string>> = {
  review: "Shipped",
  escalation: "Raised",
  health: "Detected",
  gate: "Proposed",
};

/** The hover decision per kind, mapped to the shared row's action shape. */
export function workItemActions(item: WorkItem, run: () => void): WorkAction[] {
  switch (item.kind) {
    case "review":
      return [
        { icon: Check, label: "Verify", onClick: run },
        { icon: ExternalLink, label: "Open", onClick: run },
      ];
    case "mention":
      return [
        { icon: CornerUpLeft, label: "Reply", onClick: run },
        { icon: ExternalLink, label: "Open", onClick: run },
      ];
    case "suggestion":
      return [{ icon: ArrowRight, label: "Do it", onClick: run }];
    case "gate":
      return [{ icon: ArrowRight, label: "Decide", onClick: run }];
    case "escalation":
    case "health":
      return [{ icon: ExternalLink, label: "Open", onClick: run }];
    default:
      return [{ icon: ExternalLink, label: "Open", onClick: run }];
  }
}

/**
 * Does this item need a person, and how loud? A task in `review`, an escalation,
 * and a launch gate have STOPPED on you (blocked, with the action as the label);
 * a high/medium-severity status-less item is a notice (broken/at-risk). A passing
 * suggestion, a plain mention, an in-flight task need nobody — no mark.
 */
function workItemAttention(item: WorkItem): WorkRowModel["attention"] {
  if (item.status === "review") return { label: "Needs your verify", blocked: true };
  const level = item.intent === "danger" ? "danger" : "warning";
  if (item.kind === "escalation") return { label: "Needs your reply", level, blocked: true };
  if (item.kind === "gate") return { label: "Needs your decision", level: "warning", blocked: true };
  if (item.intent === "danger" || item.intent === "warning") {
    // The notice names the kind — a health alert says "Health problem", not the
    // action words a blocked row carries. Every KIND_SIGNIFIER is a glyph, so
    // its label is always the kind noun.
    const sig = KIND_SIGNIFIER[item.kind];
    return { label: sig.as === "glyph" ? sig.label : "At risk", level };
  }
  return undefined;
}

/** Which board column / status-filter bucket a work item falls into. Real tasks
 *  and reviews use their own status; status-less items map to Open ("todo") —
 *  the "map to nearest status" board rule (Matt, 2026-07-16). */
export function workItemBucket(item: WorkItem): WorkStatus {
  return item.status ?? "todo";
}

export function workItemToRow(item: WorkItem, onOpen: (item: WorkItem) => void): WorkRowModel {
  const verb = KIND_DATE_VERB[item.kind] ?? "Updated";
  return {
    id: item.id,
    // A task/review shows its lifecycle ring; a status-less item leads with its
    // kind glyph, which is also what tells a mention from a health alert in a
    // mixed list. The two never both render — the glyph fills the slot the ring
    // would use — so the title's left edge stays put down the list.
    signifier: item.status ? undefined : KIND_SIGNIFIER[item.kind],
    state: item.status ? { as: "status", status: item.status } : undefined,
    title: item.title,
    attention: workItemAttention(item),
    subtitle: item.why,
    // Always a date stamp ("Jul 20"), matching the task rows this feed
    // interleaves with — one format, one fixed column (Matt, 2026-07-20).
    date: item.ts ? { text: formatDayMonth(item.ts), hint: `${verb} · ${formatDateTime(item.ts)}` } : undefined,
    actions: workItemActions(item, () => onOpen(item)),
    onOpen: () => onOpen(item),
  };
}
